import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE } from "../../lib/api";
import { StatusBadge, InitialsBadge } from "../../components/DashboardLayout";
import { ArrowLeft, FileText, Mail, Phone, Plus, Save, Trash2, Upload, UserPlus, Download, Copy, Check, KeyRound } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [docs, setDocs] = useState([]);
    const [notes, setNotes] = useState("");
    const [statut, setStatut] = useState("en_cours");
    const [saving, setSaving] = useState(false);
    const [newDocOpen, setNewDocOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [invite, setInvite] = useState(null);
    const [pendingReset, setPendingReset] = useState(null);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const [c, d, r] = await Promise.all([
                api.get(`/clients/${id}`),
                api.get(`/clients/${id}/documents`),
                api.get(`/password-reset-requests`, { params: { client_id: id } }),
            ]);
            setClient(c.data);
            setDocs(d.data);
            setNotes(c.data.notes || "");
            setStatut(c.data.statut);
            setPendingReset((r.data && r.data[0]) || null);
        } catch {
            toast.error("Erreur de chargement");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/clients/${id}`, { notes, statut });
            toast.success("Modifications enregistrées");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const removeClient = async () => {
        if (!window.confirm("Supprimer ce client et tous ses documents/RDV ?")) return;
        try {
            await api.delete(`/clients/${id}`);
            toast.success("Client supprimé");
            navigate("/dashboard");
        } catch {
            toast.error("Erreur");
        }
    };

    const addDoc = async (payload) => {
        try {
            await api.post(`/clients/${id}/documents`, payload);
            toast.success("Document ajouté");
            setNewDocOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const removeDoc = async (docId) => {
        if (!window.confirm("Supprimer ce document ?")) return;
        try {
            await api.delete(`/documents/${docId}`);
            load();
        } catch {
            toast.error("Erreur");
        }
    };

    const uploadFile = async (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Fichier trop volumineux (max 10 Mo)");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(
                `/clients/${id}/documents/upload?doc_type=autre&title=${encodeURIComponent(file.name)}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            toast.success("Fichier envoyé");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const doInvite = async () => {
        try {
            const { data } = await api.post(`/clients/${id}/invite`);
            setInvite(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur d'invitation");
        }
    };

    const downloadUrl = (doc) => {
        const token = localStorage.getItem("vc_access_token");
        return `${API_BASE}/documents/${doc.id}/download?auth=${encodeURIComponent(token || "")}`;
    };

    if (!client) return <div className="text-white/60 text-sm">Chargement…</div>;

    return (
        <div data-testid="client-detail" className="space-y-8 max-w-5xl">
            <Toaster position="top-center" theme="dark" richColors />
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/60 hover:text-ice text-sm">
                <ArrowLeft size={16} /> Tous les clients
            </Link>

            <div className="flex flex-wrap items-center gap-4">
                <InitialsBadge value={client.initials} size="lg" />
                <div className="flex-1">
                    <h1 className="font-serif text-3xl md:text-4xl">
                        {client.prenom} {client.nom}
                    </h1>
                    <p className="text-white/60 mt-1 text-sm">{client.niveau}</p>
                </div>
                <StatusBadge status={client.statut} />
                <button
                    onClick={doInvite}
                    data-testid="btn-invite-client"
                    className={`inline-flex items-center gap-2 text-sm transition-colors px-4 py-2.5 rounded-md ${
                        pendingReset
                            ? "bg-ice text-ink-900 hover:bg-white"
                            : "bg-ice/15 border border-ice/40 text-ice hover:bg-ice hover:text-ink-900"
                    }`}
                >
                    {pendingReset ? (
                        <>
                            <KeyRound size={16} /> Générer un nouveau mot de passe
                        </>
                    ) : (
                        <>
                            <UserPlus size={16} /> Inviter au portail
                        </>
                    )}
                </button>
                <button
                    onClick={removeClient}
                    data-testid="btn-delete-client"
                    className="p-2.5 text-white/40 hover:text-red-400 transition-colors border border-white/10 hover:border-red-400/40 rounded-md"
                    title="Supprimer le client"
                >
                    <Trash2 size={18} />
                </button>
            </div>

            {pendingReset && (
                <div
                    data-testid="pending-reset-banner"
                    className="bg-ice/10 border border-ice/40 rounded-xl p-5 flex items-start gap-3"
                >
                    <div className="w-10 h-10 rounded-full bg-ice/20 text-ice flex items-center justify-center shrink-0">
                        <KeyRound size={18} />
                    </div>
                    <div className="flex-1">
                        <div className="font-serif text-lg">Mot de passe oublié</div>
                        <div className="text-sm text-white/75 mt-1">
                            {client.prenom} a demandé un nouveau mot de passe (
                            {new Date(pendingReset.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                            ). Cliquez sur <strong>« Générer un nouveau mot de passe »</strong> ci-dessus pour lui en envoyer un.
                        </div>
                    </div>
                </div>
            )}

            {invite && <InviteResult invite={invite} onClose={() => setInvite(null)} />}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Documents + Notes */}
                <div className="lg:col-span-2 space-y-6">
                    <section className="bg-ink-600 border border-white/10 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-serif text-2xl">Documents</h2>
                            <button
                                onClick={() => setNewDocOpen(true)}
                                data-testid="btn-add-doc"
                                className="inline-flex items-center gap-2 text-ice hover:text-white text-sm"
                            >
                                <Plus size={16} /> Ajouter
                            </button>
                        </div>
                        <div className="space-y-2">
                            {docs.length === 0 && <div className="text-white/50 text-sm">Aucun document.</div>}
                            {docs.map((d) => (
                                <div
                                    key={d.id}
                                    className="flex items-center gap-3 p-3 rounded-md bg-ink-800 border border-white/5 hover:border-ice/30 transition-colors"
                                >
                                    <FileText size={18} className="text-ice shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{d.title}</div>
                                        <div className="text-xs text-white/50">
                                            {d.note || d.type.replace("_", " ")} · {new Date(d.created_at).toLocaleDateString("fr-FR")}
                                            {d.uploaded_by === "client" && (
                                                <span className="ml-2 text-ice">· Déposé par le client</span>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-xs text-white/50 hidden sm:block uppercase tracking-wider">
                                        {d.type.replace("_", " ")}
                                    </span>
                                    {d.storage_path && (
                                        <a
                                            href={downloadUrl(d)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-ice hover:text-white p-1.5"
                                            title="Télécharger"
                                        >
                                            <Download size={15} />
                                        </a>
                                    )}
                                    <button
                                        onClick={() => removeDoc(d.id)}
                                        className="text-white/40 hover:text-red-400 p-1.5"
                                        title="Supprimer"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {newDocOpen && <NewDocForm onCancel={() => setNewDocOpen(false)} onCreate={addDoc} />}
                        {!newDocOpen && (
                            <div
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    uploadFile(e.dataTransfer.files?.[0]);
                                }}
                                className="mt-4 w-full border-2 border-dashed border-white/15 rounded-md py-6 text-white/50 hover:text-ice hover:border-ice/40 transition-colors flex flex-col items-center gap-3 text-sm cursor-pointer"
                                onClick={() => fileRef.current?.click()}
                            >
                                <Upload size={18} />
                                {uploading ? "Envoi…" : "Glisser un fichier ici ou cliquer pour choisir"}
                                <div className="text-xs text-white/40">PDF, DOCX, JPG, PNG — max 10 Mo</div>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt"
                                    className="hidden"
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => uploadFile(e.target.files?.[0])}
                                />
                            </div>
                        )}
                        <button
                            onClick={() => setNewDocOpen(true)}
                            className="mt-3 text-xs text-ice hover:text-white underline underline-offset-4"
                        >
                            Ou créer une fiche document sans fichier
                        </button>
                    </section>

                    <section className="bg-ink-600 border border-white/10 rounded-xl p-6">
                        <h2 className="font-serif text-2xl mb-4">Notes de suivi</h2>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="client-notes"
                            rows={6}
                            placeholder="Vos notes de suivi pour ce client…"
                            className="w-full bg-ink-800 border border-white/10 text-white focus:border-ice focus:ring-1 focus:ring-ice rounded-md px-4 py-3 outline-none text-sm resize-none"
                        />
                    </section>
                </div>

                {/* Right: status, infos */}
                <div className="space-y-6">
                    <section className="bg-ink-600 border border-white/10 rounded-xl p-6">
                        <h2 className="font-serif text-xl mb-4">Statut du parcours</h2>
                        <select
                            value={statut}
                            onChange={(e) => setStatut(e.target.value)}
                            data-testid="client-statut-select"
                            className="w-full bg-ink-800 border border-white/15 text-white focus:border-ice rounded-md px-4 py-2.5 text-sm outline-none"
                        >
                            <option value="en_cours">En cours</option>
                            <option value="depart">Départ</option>
                            <option value="finalise">Finalisé</option>
                        </select>
                    </section>

                    <section className="bg-ink-600 border border-white/10 rounded-xl p-6">
                        <h2 className="font-serif text-xl mb-4">Infos clients</h2>
                        <div className="space-y-3 text-sm">
                            <div className="flex items-start gap-2.5">
                                <Mail size={15} className="text-ice mt-0.5" />
                                <a href={`mailto:${client.email}`} className="text-white/85 hover:text-ice break-all">
                                    {client.email}
                                </a>
                            </div>
                            {client.parent_email && (
                                <div className="flex items-start gap-2.5">
                                    <Mail size={15} className="text-ice/60 mt-0.5" />
                                    <a href={`mailto:${client.parent_email}`} className="text-white/70 hover:text-ice break-all">
                                        {client.parent_email}
                                    </a>
                                </div>
                            )}
                            {client.telephone && (
                                <div className="flex items-start gap-2.5">
                                    <Phone size={15} className="text-ice mt-0.5" />
                                    <a href={`tel:${client.telephone}`} className="text-white/85 hover:text-ice">
                                        {client.telephone}
                                    </a>
                                </div>
                            )}
                            <div className="pt-3 mt-3 border-t border-white/10 flex items-center gap-3">
                                <span className="text-xs text-white/50 uppercase tracking-wider">Suivi par</span>
                                <InitialsBadge value={client.suivi_par} size="sm" tone="ice" />
                            </div>
                        </div>
                    </section>

                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="btn-save-client"
                        className="w-full inline-flex items-center justify-center gap-2 bg-ice text-ink-900 hover:bg-white disabled:opacity-60 transition-colors px-6 py-3 rounded-md font-medium"
                    >
                        <Save size={16} /> {saving ? "Enregistrement…" : "Sauvegarder"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function NewDocForm({ onCancel, onCreate }) {
    const [title, setTitle] = useState("");
    const [type, setType] = useState("compte_rendu");
    const [note, setNote] = useState("");
    return (
        <div className="mt-4 p-4 bg-ink-800 border border-ice/20 rounded-md space-y-3">
            <input
                value={title}
                placeholder="Titre du document"
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-ink-700 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:border-ice outline-none"
            />
            <div className="grid grid-cols-2 gap-3">
                <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="bg-ink-700 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:border-ice outline-none"
                >
                    <option value="proposition">Proposition</option>
                    <option value="contrat">Contrat</option>
                    <option value="compte_rendu">Compte-rendu</option>
                    <option value="autre">Autre</option>
                </select>
                <input
                    value={note}
                    placeholder="Note (ex. Séance du 30/04)"
                    onChange={(e) => setNote(e.target.value)}
                    className="bg-ink-700 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:border-ice outline-none"
                />
            </div>
            <div className="flex gap-2 justify-end">
                <button onClick={onCancel} type="button" className="text-sm text-white/60 hover:text-white px-3 py-1.5">
                    Annuler
                </button>
                <button
                    onClick={() => title && onCreate({ title, type, note })}
                    type="button"
                    className="text-sm bg-ice text-ink-900 hover:bg-white px-4 py-1.5 rounded-md"
                >
                    Ajouter
                </button>
            </div>
        </div>
    );
}


function InviteResult({ invite, onClose }) {
    const [copied, setCopied] = useState(false);
    const text = `Bonjour,

Votre conseillère vous invite à accéder à votre espace personnel Vecteurs Conseil.

Identifiants de connexion :
• Email : ${invite.email}
• Mot de passe temporaire : ${invite.temp_password}

Connectez-vous sur : ${window.location.origin}/login

À bientôt,
L'équipe Vecteurs Conseil`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Impossible de copier");
        }
    };

    return (
        <div data-testid="invite-result" className="bg-ice/10 border border-ice/40 rounded-xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-serif text-xl text-ice">Invitation créée</h3>
                    <p className="text-sm text-white/75 mt-1">
                        Transmettez ces identifiants au client. Le mot de passe temporaire ne sera plus affiché.
                    </p>
                </div>
                <button onClick={onClose} className="text-white/50 hover:text-white text-sm">
                    Fermer
                </button>
            </div>
            <div className="bg-ink-800 rounded-md p-4 space-y-2 text-sm">
                <div><span className="text-white/55 w-28 inline-block">Email :</span> <span className="text-white font-mono">{invite.email}</span></div>
                <div><span className="text-white/55 w-28 inline-block">Mot de passe :</span> <span className="text-ice font-mono font-semibold">{invite.temp_password}</span></div>
                <div><span className="text-white/55 w-28 inline-block">URL :</span> <span className="text-white/85 font-mono">{window.location.origin}/login</span></div>
            </div>
            <button
                onClick={copy}
                data-testid="invite-copy"
                className="inline-flex items-center gap-2 text-sm bg-ice text-ink-900 hover:bg-white transition-colors px-4 py-2 rounded-md font-medium"
            >
                {copied ? <Check size={15} /> : <Copy size={15} />}
                {copied ? "Copié" : "Copier le message pour le client"}
            </button>
        </div>
    );
}
